import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API, waLink } from "../config";

const EASE = [0.16, 1, 0.3, 1];
const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: EASE },
};

const GOALS = [
  "Hipertrofia / Ganho de massa",
  "Emagrecimento / Definição",
  "Recomposição corporal",
  "Performance / Saúde",
];

const PERKS = [
  "Resposta em até 24h",
  "Protocolo pronto em até 48h",
  "Tudo no app da Prime Coaching",
  "Sem contrato de fidelidade",
];

const labelCls =
  "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50";
const fieldCls =
  "rounded-none border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-white/40 h-12";

const EMPTY = { name: "", email: "", phone: "", goal: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.goal) {
      toast.error("Selecione o seu objetivo principal.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada! Respondemos no seu WhatsApp em até 24h.");
      setForm(EMPTY);
    } catch {
      toast.error("Não foi possível enviar. Tente novamente ou chame no WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="section-pad border-t border-white/10">
      <div className="container-x grid gap-14 lg:grid-cols-2">
        {/* Left — pitch */}
        <motion.div {...reveal}>
          <p className="overline-label">Contato</p>
          <h2 className="h2-display mt-4 text-white">
            Pronto para
            <br />
            <span className="text-stroke">começar?</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
            Preencha o formulário e comece a sua transformação
            com a LF TEAM — ou chame direto no WhatsApp e comece hoje.
          </p>
          <ul className="mt-8 flex flex-col gap-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-white/75 md:text-base">
                <Check className="h-4 w-4 shrink-0" />
                {p}
              </li>
            ))}
          </ul>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="btn-outline mt-10"
          >
            <MessageCircle className="h-5 w-5" />
            Chamar no WhatsApp
          </a>
        </motion.div>

        {/* Right — form */}
        <motion.form
          {...reveal}
          onSubmit={submit}
          className="border border-white/10 bg-card p-7 md:p-10"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="lead-name" className={labelCls}>Nome completo</label>
              <Input
                id="lead-name"
                data-testid="contact-form-name"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                placeholder="Seu nome"
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="lead-email" className={labelCls}>E-mail</label>
              <Input
                id="lead-email"
                data-testid="contact-form-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="voce@email.com"
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="lead-phone" className={labelCls}>WhatsApp / Celular</label>
              <Input
                id="lead-phone"
                data-testid="contact-form-phone"
                type="tel"
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder="(11) 99999-9999"
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="lead-goal" className={labelCls}>Objetivo principal</label>
              <Select value={form.goal} onValueChange={(v) => setForm((f) => ({ ...f, goal: v }))}>
                <SelectTrigger
                  id="lead-goal"
                  data-testid="contact-form-goal"
                  className={`${fieldCls} w-full`}
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-white/15 bg-[#141416] text-white">
                  {GOALS.map((g) => (
                    <SelectItem key={g} value={g} className="rounded-none">
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="lead-message" className={labelCls}>
              Conte sua rotina e experiência (opcional)
            </label>
            <Textarea
              id="lead-message"
              data-testid="contact-form-message"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Ex: treino há 1 ano, quero ganhar massa e organizar a alimentação..."
              className="rounded-none border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-white/40"
            />
          </div>

          <button
            type="submit"
            data-testid="contact-form-submit"
            disabled={loading}
            className="btn-primary mt-8 w-full disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Enviando...
              </>
            ) : (
              "Enviar mensagem"
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
